import { Navigate, RouteObject } from 'react-router-dom';
import { lazyWithRetry } from '../shared/utils/lazy-with-retry';
import { createSuspendedRoute } from '../shared/utils/suspended-route.utils';

const BusinessManagement = lazyWithRetry(
  () => import('../pages/private/sadmin/business-management.page')
);

const SadminNotificationsPage = lazyWithRetry(
  () => import('../pages/private/sadmin/notifications.page')
);

const EcfCertification = lazyWithRetry(
  () => import('../pages/private/sadmin/ecf-certification.page')
);

const UserManagement = lazyWithRetry(
  () => import('../pages/private/sadmin/user-management.page')
);

const ContractsManagement = lazyWithRetry(
  () => import('../pages/private/sadmin/contracts.page')
);

const CouponsManagement = lazyWithRetry(
  () => import('../pages/private/sadmin/coupons.page')
);

const SystemAnnouncementsManagement = lazyWithRetry(
  () => import('../pages/private/sadmin/system-announcements.page')
);

const BusinessFeatures = lazyWithRetry(
  () => import('../pages/private/sadmin/business-features.page')
);

const BusinessComposition = lazyWithRetry(
  () =>
    import(
      '../pages/private/sadmin/business-composition/business-composition.page'
    )
);

const SadminBilling = lazyWithRetry(
  () => import('../pages/private/sadmin/billing/sadmin-billing.page')
);

const FinancialEventsStatus = lazyWithRetry(
  () => import('../pages/private/sadmin/financial-events-status.page')
);

const ProductCatalog = lazyWithRetry(
  () => import('../pages/private/sadmin/product-catalog/product-catalog.page')
);

const ProductModules = lazyWithRetry(
  () => import('../pages/private/sadmin/product-modules/product-modules.page')
);

const ModuleFeatures = lazyWithRetry(
  () => import('../pages/private/sadmin/module-features/module-features.page')
);

const AddonCatalog = lazyWithRetry(
  () => import('../pages/private/sadmin/addon-catalog/addon-catalog.page')
);

const PricingCatalog = lazyWithRetry(
  () => import('../pages/private/sadmin/pricing-catalog/pricing-catalog.page')
);

const BundleCatalog = lazyWithRetry(
  () => import('../pages/private/sadmin/bundle-catalog/bundle-catalog.page')
);

const BundleItems = lazyWithRetry(
  () => import('../pages/private/sadmin/bundle-catalog/bundle-items.page')
);

const PlanTemplateCatalog = lazyWithRetry(
  () =>
    import(
      '../pages/private/sadmin/plan-template-catalog/plan-template-catalog.page'
    )
);

const PlanTemplateItems = lazyWithRetry(
  () =>
    import(
      '../pages/private/sadmin/plan-template-catalog/plan-template-items.page'
    )
);

const SadminUsage = lazyWithRetry(
  () => import('../pages/private/sadmin/usage/usage.page')
);

const SadminSettings = lazyWithRetry(
  () => import('../pages/private/sadmin/sadmin-settings.page')
);

const SadminCrons = lazyWithRetry(
  () => import('../pages/private/sadmin/crons.page')
);

const SadminResellers = lazyWithRetry(
  () => import('../pages/private/sadmin/resellers/resellers.page')
);

const SadminResellerDetail = lazyWithRetry(
  () => import('../pages/private/sadmin/resellers/reseller-detail.page')
);

const SadminResellerPayouts = lazyWithRetry(
  () => import('../pages/private/sadmin/resellers/payouts.page')
);

const SadminResellerDeadLetter = lazyWithRetry(
  () => import('../pages/private/sadmin/resellers/dead-letter.page')
);

export const sadminRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="businesses" replace />,
  },
  {
    path: 'businesses',
    element: createSuspendedRoute(<BusinessManagement />),
  },
  {
    path: 'notifications',
    element: createSuspendedRoute(<SadminNotificationsPage />),
  },
  // /sadmin/plan-features retired (replaced by /sadmin/plan-template-
  // catalog). Redirect any deep link to the catalog group landing
  // page so bookmarks / external references don't 404.
  {
    path: 'plan-features',
    element: <Navigate to="/sadmin/plan-template-catalog" replace />,
  },
  {
    path: 'ecf-certification',
    element: createSuspendedRoute(<EcfCertification />),
  },
  {
    path: 'users',
    element: createSuspendedRoute(<UserManagement />),
  },
  {
    path: 'contracts',
    element: createSuspendedRoute(<ContractsManagement />),
  },
  {
    path: 'coupons',
    element: createSuspendedRoute(<CouponsManagement />),
  },
  {
    path: 'system-announcements',
    element: createSuspendedRoute(<SystemAnnouncementsManagement />),
  },
  {
    path: 'business-features',
    element: createSuspendedRoute(<BusinessFeatures />),
  },
  {
    path: 'business-composition',
    element: createSuspendedRoute(<BusinessComposition />),
  },
  {
    path: 'billing',
    element: createSuspendedRoute(<SadminBilling />),
  },
  {
    path: 'financial-events',
    element: createSuspendedRoute(<FinancialEventsStatus />),
  },
  {
    path: 'product-catalog',
    element: createSuspendedRoute(<ProductCatalog />),
  },
  {
    path: 'product-catalog/:productId/modules',
    element: createSuspendedRoute(<ProductModules />),
  },
  {
    path: 'product-catalog/:productId/modules/:moduleId/features',
    element: createSuspendedRoute(<ModuleFeatures />),
  },
  {
    path: 'addon-catalog',
    element: createSuspendedRoute(<AddonCatalog />),
  },
  {
    path: 'pricing-catalog',
    element: createSuspendedRoute(<PricingCatalog />),
  },
  {
    path: 'bundle-catalog',
    element: createSuspendedRoute(<BundleCatalog />),
  },
  {
    path: 'bundle-catalog/:bundleId/items',
    element: createSuspendedRoute(<BundleItems />),
  },
  {
    path: 'plan-template-catalog',
    element: createSuspendedRoute(<PlanTemplateCatalog />),
  },
  {
    path: 'plan-template-catalog/:planTemplateId/items',
    element: createSuspendedRoute(<PlanTemplateItems />),
  },
  {
    path: 'usage',
    element: createSuspendedRoute(<SadminUsage />),
  },
  {
    path: 'settings',
    element: createSuspendedRoute(<SadminSettings />),
  },
  {
    path: 'crons',
    element: createSuspendedRoute(<SadminCrons />),
  },
  {
    path: 'resellers',
    element: createSuspendedRoute(<SadminResellers />),
  },
  {
    path: 'resellers/:id',
    element: createSuspendedRoute(<SadminResellerDetail />),
  },
  {
    path: 'reseller-payouts',
    element: createSuspendedRoute(<SadminResellerPayouts />),
  },
  {
    path: 'reseller-commissions/dead-letter',
    element: createSuspendedRoute(<SadminResellerDeadLetter />),
  },
];
